import { getReservationGes } from '../ReservationModule/shared/services/data.service';
import { ReservationStatus } from '../shared/enums/reservation-status';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { ReservationUiModel } from '../shared/ui-models/reservation.ui-model';

export type GesTabKey = 'today' | 'attention' | 'upcoming';
export type QueueType = 'Arrival' | 'Departure' | 'General';
export type AccessProvisioningStatus =
  | 'In-Progress'
  | 'Done'
  | 'Pending Option 1'
  | 'Pending Option 2';

export interface GesActionState {
  accessProvisioningStatus: AccessProvisioningStatus;
  isUnitInspected: boolean;
  inspectionDate: number | null;
  isUnitCleaned: boolean;
  gesCheckInAt: number | null;
  gesCheckOutAt: number | null;
}

export interface InspectionModalState {
  reservationId: string;
  amenitiesPhoto: File | null;
  bedPhoto: File | null;
  toiletPhoto: File | null;
  livingAreaPhoto: File | null;
}

export interface ComputedInspectionState {
  inspectionDate: number | null;
  isUnitInspected: boolean;
}

// Button visibility logic
export interface GesButtonVisibility {
  showCheckIn: boolean;
  showCheckInWithoutInspection: boolean;
  showCheckOut: boolean;
  showInspectionActions: boolean;
}

export const computeButtonVisibility = (
  item: ReservationItem,
  isUnitInspected: boolean,
  gesCheckInAt: number | null,
  actualCheckInAt?: number | null
): GesButtonVisibility => {
  const isArrival = item.queueType === 'Arrival';
  const isDeparture = item.queueType === 'Departure';

  // Check In: Show only if isUnitInspected = true
  const showCheckIn = isArrival && isUnitInspected;

  // Check In Without Inspection: Show only if isUnitInspected = false
  const showCheckInWithoutInspection = isArrival && !isUnitInspected;

  const showCheckOut = isDeparture && !!(gesCheckInAt || actualCheckInAt);

  // Inspection Done/Rejected: Show only for Arrival where unit not yet inspected
  const showInspectionActions = isArrival && !isUnitInspected;

  return {
    showCheckIn,
    showCheckInWithoutInspection,
    showCheckOut,
    showInspectionActions
  };
};

export interface ReservationItem {
  id: string;
  reservationId: string;
  area: string;
  project: string;
  roomName: string;
  guestName: string;
  phone: string;
  checkIn: string;
  checkOut: string;
  nights: string;
  status: string;
  source: string;
  queueType: QueueType;
  // GES
  unitInspectedAt?: number;
  unitInspectedByUsername?: string;
  unitInspectedById?: string;
  unitInspectionRejectedAt?: number;
  unitInspectionRejectedByUsername?: string;
  unitInspectionRejectedById?: string;
  isUnitInspected?: boolean;
  accessProvisioning?: string;
  actualCheckInAt?: number;
  actualCheckOutAt?: number;
}

export interface TabGroup {
  title: string;
  items: ReservationItem[];
  loading: boolean;
  error?: string;
}

export type TabData = Record<GesTabKey, TabGroup>;

// Action state helpers
export const createInitialActionState = (): GesActionState => ({
  accessProvisioningStatus: 'In-Progress',
  isUnitInspected: false,
  inspectionDate: null,
  isUnitCleaned: false,
  gesCheckInAt: null,
  gesCheckOutAt: null
});

export const formatTimestamp = (value: number | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-GB');
};

export const getQueuePillClass = (type: QueueType): string => {
  switch (type) {
    case 'Arrival':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Departure':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return 'bg-gray-50 text-neutral-600 border-gray-200';
  }
};

// GES Action Payload Types
export type GesActionType =
  | 'check_in'
  | 'check_in_without_inspection'
  | 'check_out'
  | 'access_provisioning'
  | 'inspection_done'
  | 'inspection_rejected';

export interface GesActionPayload {
  actualCheckInAt?: number;
  actualCheckOutAt?: number;
  isUnitInspected?: boolean;
  isUnitCleaned?: boolean;
  accessProvisioning?: string;
}

export interface GesToastMessages {
  loadingMessage: string;
  successMessage: string;
}

export interface GesActionConfig {
  gesPayload: GesActionPayload;
  loadingMessage: string;
  successMessage: string;
}

export const getGesActionConfig = (
  action: GesActionType,
  payload: Record<string, any>
): GesActionConfig => {
  switch (action) {
    case 'check_in':
      return {
        gesPayload: { actualCheckInAt: payload.actualCheckInAt },
        loadingMessage: 'Checking in...',
        successMessage: 'Checked in successfully'
      };
    case 'check_in_without_inspection':
      return {
        gesPayload: { actualCheckInAt: payload.actualCheckInAt },
        loadingMessage: 'Checking in without inspection...',
        successMessage: 'Checked in without inspection'
      };
    case 'check_out':
      return {
        gesPayload: { actualCheckOutAt: payload.actualCheckOutAt },
        loadingMessage: 'Checking out...',
        successMessage: 'Checked out successfully'
      };
    case 'access_provisioning':
      return {
        gesPayload: { accessProvisioning: payload.accessProvisioningStatus },
        loadingMessage: 'Updating access provisioning...',
        successMessage: 'Access provisioning updated'
      };
    case 'inspection_done':
      return {
        gesPayload: { isUnitInspected: true, isUnitCleaned: true },
        loadingMessage: 'Saving inspection...',
        successMessage: 'Inspection completed successfully'
      };
    case 'inspection_rejected':
      return {
        gesPayload: { isUnitInspected: false, isUnitCleaned: false },
        loadingMessage: 'Saving inspection rejection...',
        successMessage: 'Inspection rejected'
      };
    default:
      return {
        gesPayload: {},
        loadingMessage: 'Processing...',
        successMessage: 'Action completed'
      };
  }
};

// Component prop interfaces
export interface TodayActionsProps {
  item: ReservationItem;
  actionState: GesActionState;
  onAccessChange: (reservationId: string, status: AccessProvisioningStatus) => void;
  onCheckIn: (reservationId: string, withInspection: boolean) => void;
  onCheckOut: (reservationId: string) => void;
  onInspectionDone: (reservationId: string) => void;
  onInspectionRejected: (reservationId: string) => void;
}

export interface ReservationCardProps {
  item: ReservationItem;
  showQueue: boolean;
  showTodayActions: boolean;
  showUpcomingActions: boolean;
  actionState: GesActionState;
  onAccessChange: (reservationId: string, status: AccessProvisioningStatus) => void;
  onCheckIn: (reservationId: string, withInspection: boolean) => void;
  onCheckOut: (reservationId: string) => void;
  onInspectionDone: (reservationId: string) => void;
  onInspectionRejected: (reservationId: string) => void;
}

// Tab data helpers
const formatDate = (date: number | string | Date): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB');
};

const toStatusLabel = (status: string): string => {
  switch (status) {
    case ReservationStatus.CHECKED_IN:
      return 'Checked in';
    case ReservationStatus.CHECKED_OUT:
      return 'Checked out';
    case ReservationStatus.CONFIRMATION_PENDING:
      return 'Confirmation pending';
    case ReservationStatus.IN_HOUSE:
      return 'In house';
    case ReservationStatus.CHECKED_IN_STARTED:
      return 'Check-in started';
    case ReservationStatus.PENDING_CHECK_IN:
      return 'Pending check-in';
    case ReservationStatus.CANCELED:
      return 'Canceled';
    case ReservationStatus.EXPIRED:
      return 'Expired';
    default:
      return status || '-';
  }
};

const mapReservationToItem = (
  reservation: ReservationUiModel,
  queueType: QueueType = 'General'
): ReservationItem => {
  return {
    id: reservation.id,
    reservationId: String(reservation.id || '-'),
    area: reservation.area || '-',
    project: reservation.propertyName || '-',
    roomName: reservation.roomName || '-',
    guestName: reservation.primaryGuestName || '-',
    phone:
      (reservation as any).primaryGuestPhone ||
      (reservation as any).phoneNumber ||
      (reservation as any).phone ||
      '-',
    checkIn: formatDate(reservation.checkInDate),
    checkOut: formatDate(reservation.checkOutDate),
    nights: String((reservation as any).numberOfNights ?? '-'),
    status: toStatusLabel(reservation.status),
    source: reservation.source || '-',
    queueType,
    // GES
    unitInspectedAt: reservation.unitInspectedAt,
    unitInspectedByUsername: reservation.unitInspectedByUsername,
    unitInspectedById: reservation.unitInspectedById,
    unitInspectionRejectedAt: reservation.unitInspectionRejectedAt,
    unitInspectionRejectedByUsername: reservation.unitInspectionRejectedByUsername,
    unitInspectionRejectedById: reservation.unitInspectionRejectedById,
    isUnitInspected: reservation.isUnitInspected,
    accessProvisioning: reservation.accessProvisioning,
    actualCheckInAt: reservation.actualCheckInAt,
    actualCheckOutAt: reservation.actualCheckOutAt
  };
};

export const getStatusStyles = (status?: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'check in') {
    return 'bg-primary-50 text-primary-600 border-primary-100';
  }
  if (s === 'not check in') {
    return 'bg-yellow-50 text-yellow-600 border-yellow-300';
  }
  if (s === 'check out') {
    return 'bg-gray-100 text-neutral-800 border-gray-200';
  }

  return 'bg-blue-50 text-blue-600 border-blue-100';
};

export const createInitialTabData = (): TabData => ({
  today: {
    title: "Today's Arrivals & Departures",
    items: [],
    loading: false
  },
  attention: {
    title: 'Overdue Check-in / Check-out',
    items: [],
    loading: false
  },
  upcoming: {
    title: 'Upcoming Arrivals (Next 30 Days)',
    items: [],
    loading: false
  }
});

const getErrorIfNoItems = (
  error: string | undefined,
  items: ReservationItem[],
  message: string
): string | undefined => {
  return error && items.length === 0 ? message : undefined;
};

type GesRange = 'TODAY' | 'ATTENTION-REQUIRED' | 'UPCOMING';

const getQueueTypeFromEventType = (eventType?: 'arrival' | 'departure'): QueueType => {
  if (eventType === 'arrival') return 'Arrival';
  if (eventType === 'departure') return 'Departure';
  return 'General';
};

const requestTabData = async (
  payload: { range: GesRange },
  queueType: QueueType | ((reservation: ReservationUiModel) => QueueType)
): Promise<{ items: ReservationItem[]; error?: string }> => {
  const response = await getReservationGes(payload);
  if (isErrorInfo(response)) {
    return {
      items: [],
      error: response.errorMessage || 'Failed to load reservations'
    };
  }

  return {
    items: (response.reservationList || []).map((r: ReservationUiModel) =>
      mapReservationToItem(r, typeof queueType === 'function' ? queueType(r) : queueType)
    )
  };
};

export const fetchGesTabDataByKey = async (
  tabKey: GesTabKey
): Promise<{ items: ReservationItem[]; error?: string }> => {
  switch (tabKey) {
    case 'today': {
      const result = await requestTabData(
        {
          range: 'TODAY'
        },
        (reservation) => getQueueTypeFromEventType(reservation.eventType)
      );

      return {
        items: result.items ?? [],
        error: getErrorIfNoItems(
          result.error,
          result.items ?? [],
          'Failed to load today arrivals/departures.'
        )
      };
    }
    case 'attention': {
      const result = await requestTabData(
        {
          range: 'ATTENTION-REQUIRED'
        },
        'General'
      );

      return {
        items: result.items ?? [],
        error: getErrorIfNoItems(result.error, result.items ?? [], 'Failed to load attention list.')
      };
    }
    case 'upcoming': {
      const result = await requestTabData(
        {
          range: 'UPCOMING'
        },
        'General'
      );

      return {
        items: result.items ?? [],
        error: getErrorIfNoItems(result.error, result.items ?? [], 'Failed to load upcoming list.')
      };
    }
    default:
      return {
        items: [],
        error: 'Failed to load reservations'
      };
  }
};
